import React, { useEffect, useState } from "react";
import {
  Plus, Pencil, Trash2, X, Tag, Tv2, Calendar, Signal,
  IndianRupee, TrendingDown, Zap, Layers, CheckCircle2, XCircle,
  FolderOpen, ChevronDown, ChevronRight, Package, EyeOff, Eye,
  Gift,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/DashLayout";
import { api } from "@/lib/api";
import SearchableSelect from "@/components/ui/SearchableSelect";

/* ─── helpers ────────────────────────────────────────────────────────────── */
const fmt = (v) => (v !== undefined && v !== null && v !== "" ? `₹${Number(v).toFixed(2)}` : "—");
const OPERATORS = ["Dish TV"];
const CATEGORIES = ["SD", "HD", "4K", "Kids", "Sports", "Movies", "South", "Regional", "International", "Other"];

const OFFER_TYPES = [
  { value: "manual_recharge", label: "Manual Recharge" },
  { value: "ofy_3m",          label: "OFY Offer: 3 Months pe 3 Days" },
  { value: "ofy_6m",          label: "OFY Offer: 6 Months pe 10 Days" },
  { value: "ofy_12m",         label: "OFY Offer: 12 Months pe 30 Days" },
];
const OFFER_LABEL_MAP = Object.fromEntries(OFFER_TYPES.map(o => [o.value, o.label]));

const DEFAULT_FORM = {
  name: "",
  operator: "Dish TV",
  category: "HD",
  plan_type: "both",
  group_id: "",
  base_cost: "",
  discount: "",
  recharge_amount: "",
  dealer_price: "",
  validity_days: 30,
  channel_count: "",
  description: "",
  is_active: true,
  offer_type: "manual_recharge",
};

/* ─── Modal ──────────────────────────────────────────────────────────────── */
function PlanModal({ form, setForm, onClose, onSave, editing, groups }) {
  const operatorGroups = groups.filter((g) => g.operator === form.operator && g.is_active);
  const handleChange = (field, value) =>
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Auto-derive dealer_price when base_cost or discount changes
      if (field === "base_cost" || field === "discount") {
        const bc = parseFloat(field === "base_cost" ? value : next.base_cost) || 0;
        const disc = parseFloat(field === "discount" ? value : next.discount) || 0;
        next.dealer_price = bc > 0 ? (bc - disc).toFixed(2) : "";
      }
      return next;
    });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.base_cost) return toast.error("Base cost (MRP) is required");
    onSave();
  };

  const inputCls =
    "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40 bg-white";
  const labelCls = "block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col"
        style={{ border: "1px solid #e2e8f0" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="font-heading font-bold text-xl text-brand-nav">
              {editing ? "Edit Plan / Package" : "Add New Plan / Package"}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Fill in pricing details — Dealer Price auto-updates.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto flex-1 px-6 py-5"
          id="plan-form"
        >
          {/* Row 1 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="sm:col-span-2">
              <label className={labelCls}>Plan Name *</label>
              <input
                required
                id="plan-name"
                placeholder="e.g. HD Sports Pack 365"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Operator *</label>
              <SearchableSelect
                value={form.operator}
                onChange={(val) => handleChange("operator", val)}
                options={OPERATORS.map(o => ({ label: o, value: o }))}
              />
            </div>

            <div>
              <label className={labelCls}>Category *</label>
              <SearchableSelect
                value={form.category}
                onChange={(val) => handleChange("category", val)}
                options={CATEGORIES.map(c => ({ label: c, value: c }))}
              />
            </div>
          </div>

          {/* Group selector & Plan Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={labelCls}>Product Group</label>
              <SearchableSelect
                value={form.group_id || ""}
                onChange={(val) => handleChange("group_id", val)}
                options={[{ label: "— No Group (Ungrouped) —", value: "" }, ...operatorGroups.map(g => ({ label: g.name, value: g.id }))]}
              />
              <p className="text-xs text-slate-400 mt-1">Only groups matching the selected operator are shown.</p>
            </div>
            <div>
              <label className={labelCls}>Plan Visibility</label>
              <div className="flex items-center gap-3 mt-2">
                <label className="flex items-center gap-1.5 text-sm text-slate-600">
                  <input type="radio" checked={form.plan_type === 'both'} onChange={() => handleChange("plan_type", "both")} className="text-brand-primary focus:ring-brand-primary" />
                  Both
                </label>
                <label className="flex items-center gap-1.5 text-sm text-slate-600">
                  <input type="radio" checked={form.plan_type === 'activation'} onChange={() => handleChange("plan_type", "activation")} className="text-brand-primary focus:ring-brand-primary" />
                  Activation
                </label>
                <label className="flex items-center gap-1.5 text-sm text-slate-600">
                  <input type="radio" checked={form.plan_type === 'recharge'} onChange={() => handleChange("plan_type", "recharge")} className="text-brand-primary focus:ring-brand-primary" />
                  Recharge
                </label>
              </div>
            </div>
          </div>

          {/* Pricing section */}
          <div
            className="rounded-xl p-4 mb-4"
            style={{ background: "linear-gradient(135deg,#f0f9ff 0%,#e0f2fe 100%)", border: "1px solid #bae6fd" }}
          >
            <p className="text-xs font-bold text-sky-700 uppercase tracking-widest mb-3">
              💰 Pricing Details
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className={labelCls}>Base Cost (MRP) *</label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                  <input
                    required
                    id="plan-base-cost"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="299.00"
                    value={form.base_cost}
                    onChange={(e) => handleChange("base_cost", e.target.value)}
                    className={`${inputCls} pl-6`}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">Operator's MRP</p>
              </div>

              <div>
                <label className={labelCls}>Max Discount</label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                  <input
                    id="plan-discount"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="30.00"
                    value={form.discount}
                    onChange={(e) => handleChange("discount", e.target.value)}
                    className={`${inputCls} pl-6`}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">MRP − Dealer Price</p>
              </div>

              <div>
                <label className={labelCls}>Dealer Price</label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                  <input
                    id="plan-dealer-price"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="269.00"
                    value={form.dealer_price}
                    onChange={(e) => handleChange("dealer_price", e.target.value)}
                    className={`${inputCls} pl-6`}
                  />
                </div>
                <p className="text-xs text-sky-600 font-medium mt-1">Auto-computed</p>
              </div>

              <div>
                <label className={labelCls}>Recharge Amount</label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm">₹</span>
                  <input
                    id="plan-recharge-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="299.00"
                    value={form.recharge_amount}
                    onChange={(e) => handleChange("recharge_amount", e.target.value)}
                    className={`${inputCls} pl-6`}
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">Customer pays</p>
              </div>
            </div>
          </div>

          {/* Row 3: validity, channels */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className={labelCls}>Validity (Days) *</label>
              <input
                required
                id="plan-validity"
                type="number"
                min="1"
                placeholder="30"
                value={form.validity_days}
                onChange={(e) => handleChange("validity_days", e.target.value)}
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>No. of Channels</label>
              <input
                id="plan-channels"
                type="number"
                min="0"
                placeholder="180"
                value={form.channel_count}
                onChange={(e) => handleChange("channel_count", e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className={labelCls}>Description</label>
            <textarea
              id="plan-description"
              rows={2}
              placeholder="Brief plan description shown to customers..."
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              className={`${inputCls} resize-none`}
            />
          </div>

          {/* Offer Type (DishTV API Remarks) */}
          <div
            className="rounded-xl p-4 mb-4"
            style={{ background: "linear-gradient(135deg,#fef9f0 0%,#fdf2e0 100%)", border: "1px solid #fcd9a0" }}
          >
            <p className="text-xs font-bold text-amber-700 uppercase tracking-widest mb-3 flex items-center gap-1.5">
              <Gift className="w-3.5 h-3.5" /> Offer Type (API Remarks)
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {OFFER_TYPES.map((ot) => (
                <label
                  key={ot.value}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition text-sm font-medium ${
                    form.offer_type === ot.value
                      ? "border-amber-400 bg-amber-50 text-amber-800 shadow-sm"
                      : "border-slate-200 bg-white text-slate-600 hover:border-amber-300 hover:bg-amber-50/40"
                  }`}
                >
                  <input
                    type="radio"
                    name="offer_type"
                    checked={form.offer_type === ot.value}
                    onChange={() => handleChange("offer_type", ot.value)}
                    className="text-amber-500 focus:ring-amber-400"
                  />
                  {ot.label}
                </label>
              ))}
            </div>
            <p className="text-xs text-amber-600/70 mt-2">
              This value is sent as "Remarks" in the DishTV recharge API payload.
            </p>
          </div>

          {/* Status toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => handleChange("is_active", !form.is_active)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.is_active ? "bg-brand-primary" : "bg-slate-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  form.is_active ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <span className="text-sm font-medium text-slate-600">
              {form.is_active ? "Active — visible to dealers & customers" : "Inactive — hidden"}
            </span>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="plan-form"
            className="px-5 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primaryHover rounded-lg transition shadow-sm"
          >
            {editing ? "Update Plan" : "Save Plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── PlanCard removed in favor of table view ────────────────────────────── */

/* ─── Group Modal ────────────────────────────────────────────────────────── */
function GroupModal({ gForm, setGForm, onClose, onSave, editing }) {
  const inputCls =
    "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40 bg-white";
  const labelCls = "block text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col"
        style={{ border: "1px solid #e2e8f0" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-heading font-bold text-lg text-brand-nav">
            {editing ? "Edit Product Group" : "Add Product Group"}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); if (!gForm.name.trim()) return toast.error("Name required"); onSave(); }}
          className="px-6 py-5 space-y-4"
          id="group-form"
        >
          <div>
            <label className={labelCls}>Group Name *</label>
            <input required value={gForm.name} onChange={(e) => setGForm({ ...gForm, name: e.target.value })} className={inputCls} placeholder="e.g. Base Odia Pack" />
          </div>
          <div>
            <label className={labelCls}>Operator *</label>
            <SearchableSelect
              value={gForm.operator}
              onChange={(val) => setGForm({ ...gForm, operator: val })}
              options={OPERATORS.map(o => ({ label: o, value: o }))}
            />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <input value={gForm.description} onChange={(e) => setGForm({ ...gForm, description: e.target.value })} className={inputCls} placeholder="Brief description..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Sort Order</label>
              <input type="number" value={gForm.sort_order} onChange={(e) => setGForm({ ...gForm, sort_order: parseInt(e.target.value, 10) || 0 })} className={inputCls} />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
                <input type="checkbox" checked={gForm.is_active} onChange={(e) => setGForm({ ...gForm, is_active: e.target.checked })} className="rounded" />
                Active
              </label>
            </div>
          </div>
        </form>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition">Cancel</button>
          <button type="submit" form="group-form" className="px-5 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primaryHover rounded-lg transition shadow-sm">
            {editing ? "Update Group" : "Save Group"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Products Page ─────────────────────────────────────────────────── */
export default function Products() {
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [filterOperator, setFilterOperator] = useState("All");
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterGroup, setFilterGroup] = useState("All");
  const [expandedGroups, setExpandedGroups] = useState({});

  // Group modal state
  const DEFAULT_GFORM = { name: "", operator: "Dish TV", description: "", sort_order: 0, is_active: true };
  const [gForm, setGForm] = useState(DEFAULT_GFORM);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  const load = () => {
    api.get("/admin/products").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load plans"));
    api.get("/admin/product-groups").then((r) => setGroups(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  // Auto-expand all groups on first load
  useEffect(() => {
    if (groups.length > 0 && Object.keys(expandedGroups).length === 0) {
      const exp = {};
      groups.forEach((g) => { exp[g.id] = true; });
      exp["ungrouped"] = true;
      setExpandedGroups(exp);
    }
  }, [groups]);

  const toggleGroup = (gid) => setExpandedGroups((prev) => ({ ...prev, [gid]: !prev[gid] }));

  // Product handlers
  const openAdd = () => { setForm(DEFAULT_FORM); setEditing(null); setShowModal(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name ?? "", operator: p.operator ?? "Dish TV", category: p.category ?? "HD",
      plan_type: p.plan_type ?? "both", group_id: p.group_id ?? "",
      base_cost: p.base_cost ?? "", discount: p.discount ?? "",
      recharge_amount: p.recharge_amount ?? "", dealer_price: p.dealer_price ?? "",
      validity_days: p.validity_days ?? 30, channel_count: p.channel_count ?? "",
      description: p.description ?? "", is_active: p.is_active ?? true,
      offer_type: p.offer_type ?? "manual_recharge",
    });
    setEditing(p.id);
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditing(null); setForm(DEFAULT_FORM); };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        plan_type: form.plan_type || 'both',
        group_id: form.group_id || null,
        base_cost: parseFloat(form.base_cost) || 0,
        discount: parseFloat(form.discount) || 0,
        recharge_amount: parseFloat(form.recharge_amount) || 0,
        dealer_price: parseFloat(form.dealer_price) || 0,
        validity_days: parseInt(form.validity_days, 10) || 30,
        channel_count: parseInt(form.channel_count, 10) || null,
      };
      if (editing) {
        await api.put(`/admin/products/${editing}`, payload);
        toast.success("Plan updated successfully");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Plan added successfully");
      }
      closeModal();
      load();
    } catch {
      toast.error("Failed to save plan");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Disable this plan? It will be hidden from customers and dealers.")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Plan disabled");
      load();
    } catch { toast.error("Failed to disable"); }
  };

  const handleEnable = async (id) => {
    if (!window.confirm("Enable this plan? It will become visible to customers and dealers.")) return;
    try {
      await api.patch(`/admin/products/${id}/enable`);
      toast.success("Plan enabled");
      load();
    } catch { toast.error("Failed to enable"); }
  };

  // Group handlers
  const openAddGroup = () => { setGForm(DEFAULT_GFORM); setEditingGroup(null); setShowGroupModal(true); };
  const openEditGroup = (g) => {
    setGForm({ name: g.name, operator: g.operator, description: g.description || "", sort_order: g.sort_order || 0, is_active: g.is_active ?? true });
    setEditingGroup(g.id);
    setShowGroupModal(true);
  };
  const closeGroupModal = () => { setShowGroupModal(false); setEditingGroup(null); setGForm(DEFAULT_GFORM); };

  const handleSaveGroup = async () => {
    try {
      if (editingGroup) {
        await api.put(`/admin/product-groups/${editingGroup}`, gForm);
        toast.success("Group updated");
      } else {
        await api.post("/admin/product-groups", gForm);
        toast.success("Group created");
      }
      closeGroupModal();
      load();
    } catch { toast.error("Failed to save group"); }
  };

  const handleDeleteGroup = async (gid) => {
    if (!window.confirm("Disable this group? All plans in this group will also be disabled and hidden.")) return;
    try {
      await api.delete(`/admin/product-groups/${gid}`);
      toast.success("Group disabled");
      load();
    } catch { toast.error("Failed to disable group"); }
  };

  const handleEnableGroup = async (gid) => {
    if (!window.confirm("Enable this group? All plans in this group will also be enabled and visible.")) return;
    try {
      await api.patch(`/admin/product-groups/${gid}/enable`);
      toast.success("Group enabled");
      load();
    } catch { toast.error("Failed to enable group"); }
  };

  // Filtering
  const operators = ["All", ...new Set(rows.map((r) => r.operator).filter(Boolean))];
  const categories = ["All", ...new Set(rows.map((r) => r.category).filter(Boolean))];
  const groupNames = ["All", ...groups.map((g) => g.name), "Ungrouped"];
  const filtered = rows.filter((r) => {
    const q = search.toLowerCase();
    const matchSearch = !q || r.name?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q);
    const matchOp = filterOperator === "All" || r.operator === filterOperator;
    const matchCat = filterCategory === "All" || r.category === filterCategory;
    const matchGrp = filterGroup === "All" || (filterGroup === "Ungrouped" ? !r.group_id : r.group_name === filterGroup);
    return matchSearch && matchOp && matchCat && matchGrp;
  });

  // Group products by group_id for accordion display
  const groupedProducts = {};
  groups.forEach((g) => { groupedProducts[g.id] = { group: g, products: [] }; });
  groupedProducts["ungrouped"] = { group: { id: "ungrouped", name: "Ungrouped", operator: "", product_count: 0 }, products: [] };
  filtered.forEach((p) => {
    const gid = p.group_id || "ungrouped";
    if (!groupedProducts[gid]) groupedProducts[gid] = { group: { id: gid, name: p.group_name || "Unknown", operator: p.operator }, products: [] };
    groupedProducts[gid].products.push(p);
  });

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="font-heading font-bold text-2xl text-brand-nav">Plans & Packages</h3>
          <p className="text-sm text-slate-400 mt-0.5">
            Manage DTH recharge plans organized by product groups.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={openAddGroup}
            className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white font-semibold px-4 py-2.5 rounded-xl transition shadow-sm"
          >
            <FolderOpen className="w-4 h-4" />
            Add Group
          </button>
          <button
            id="admin-add-product-btn"
            onClick={openAdd}
            className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold px-4 py-2.5 rounded-xl transition shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add Plan
          </button>
        </div>
      </div>

      {/* Product Groups Management */}
      {groups.length > 0 && (
        <div className="mb-6 rounded-xl border border-slate-200 bg-white overflow-hidden" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/80">
            <h4 className="text-sm font-bold text-slate-600 uppercase tracking-wide flex items-center gap-2">
              <Package className="w-4 h-4" /> Product Groups ({groups.length})
            </h4>
          </div>
          <div className="divide-y divide-slate-50">
            {groups.map((g) => (
              <div key={g.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50/50 transition">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: g.is_active ? "#22c55e" : "#94a3b8" }}
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-brand-nav truncate">{g.name}</p>
                    <p className="text-xs text-slate-400">{g.operator} · {g.product_count ?? 0} plans{g.description ? ` · ${g.description}` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 ml-3">
                  <button onClick={() => openEditGroup(g)} className="p-1.5 rounded-lg text-slate-400 hover:text-brand-primary hover:bg-brand-primary/10 transition" title="Edit group">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {g.is_active ? (
                    <button onClick={() => handleDeleteGroup(g.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition" title="Disable group">
                      <EyeOff className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button onClick={() => handleEnableGroup(g.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-green-500 hover:bg-green-50 transition" title="Enable group">
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <input
          type="text"
          placeholder="Search plans..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 w-48"
        />
        <div className="w-40">
          <SearchableSelect value={filterOperator} onChange={setFilterOperator} options={operators.map(o => ({ label: o, value: o }))} />
        </div>
        <div className="w-40">
          <SearchableSelect value={filterCategory} onChange={setFilterCategory} options={categories.map(c => ({ label: c, value: c }))} />
        </div>
        <div className="w-48">
          <SearchableSelect value={filterGroup} onChange={setFilterGroup} options={groupNames.map(g => ({ label: g, value: g }))} />
        </div>
        <span className="ml-auto text-xs text-slate-400 self-center">
          {filtered.length} of {rows.length} plans
        </span>
      </div>

      {/* Grouped Accordion Display */}
      <div className="space-y-4">
        {Object.entries(groupedProducts).map(([gid, { group: grp, products: prods }]) => {
          if (prods.length === 0 && gid !== "ungrouped") return null;
          if (prods.length === 0) return null;
          const isOpen = expandedGroups[gid] !== false;
          return (
            <div key={gid} className="rounded-xl border border-slate-200 bg-white overflow-hidden" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(gid)}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition text-left"
                style={{ background: gid === "ungrouped" ? "#f8fafc" : "linear-gradient(135deg,#f8faff 0%,#f1f5f9 100%)" }}
              >
                <div className="flex items-center gap-3">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  <FolderOpen className="w-4 h-4 text-brand-primary" />
                  <span className="font-heading font-bold text-sm text-brand-nav">{grp.name}</span>
                  {grp.operator && <Badge tone="info">{grp.operator}</Badge>}
                  <span className="text-xs text-slate-400 font-medium">{prods.length} plan{prods.length !== 1 ? "s" : ""}</span>
                </div>
              </button>
              {/* Products table inside group */}
              {isOpen && (
                <div className="overflow-x-auto border-t border-slate-100">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <tr>
                        <th className="px-5 py-3 font-semibold">Plan Name</th>
                        <th className="px-5 py-3 font-semibold">Details</th>
                        <th className="px-5 py-3 font-semibold">Pricing</th>
                        <th className="px-5 py-3 font-semibold">Status</th>
                        <th className="px-5 py-3 font-semibold text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {prods.map((p) => (
                        <tr key={p.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-5 py-3 align-top">
                            <p className="font-bold text-brand-nav">{p.name}</p>
                            {p.offer_type && OFFER_LABEL_MAP[p.offer_type] && (
                              <span
                                className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                                style={{
                                  background: p.offer_type === "manual_recharge" ? "#f1f5f9" : "#fef3c7",
                                  color: p.offer_type === "manual_recharge" ? "#64748b" : "#92400e",
                                }}
                              >
                                <Gift className="w-3 h-3" />
                                {OFFER_LABEL_MAP[p.offer_type]}
                              </span>
                            )}
                            <p className="text-xs text-slate-500 mt-1">{p.operator} • <span className="font-medium">{p.category}</span></p>
                            {p.description && <p className="text-xs text-slate-400 mt-1 line-clamp-1" title={p.description}>{p.description}</p>}
                          </td>
                          <td className="px-5 py-3 align-top">
                            <div className="text-xs text-slate-600 space-y-1">
                              <p className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-slate-400" /> {p.validity_days} days</p>
                              {p.channel_count > 0 && <p className="flex items-center gap-1.5"><Tv2 className="w-3.5 h-3.5 text-slate-400" /> {p.channel_count} channels</p>}
                            </div>
                          </td>
                          <td className="px-5 py-3 align-top">
                            <div className="text-xs space-y-1">
                              <p className="font-bold text-brand-primary flex items-center gap-1">
                                <Zap className="w-3.5 h-3.5" /> Recharge: {fmt(p.recharge_amount > 0 ? p.recharge_amount : p.base_cost)}
                              </p>
                              <div className="text-slate-500 font-medium space-x-2">
                                <span>MRP: {fmt(p.base_cost)}</span>
                                <span className="text-slate-300">|</span>
                                <span className="text-green-600">Dealer: {fmt(p.dealer_price > 0 ? p.dealer_price : p.base_cost)}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-3 align-top">
                            {p.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>}
                            <div className="mt-2">
                              {p.plan_type === 'activation' && <Badge tone="warning">Activation Only</Badge>}
                              {p.plan_type === 'recharge' && <Badge tone="info">Recharge Only</Badge>}
                              {(p.plan_type === 'both' || !p.plan_type) && <Badge tone="neutral">All Uses</Badge>}
                            </div>
                          </td>
                          <td className="px-5 py-3 align-top text-right">
                            <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg text-slate-400 hover:text-brand-primary hover:bg-brand-primary/10 transition" title="Edit plan">
                              <Pencil className="w-4 h-4" />
                            </button>
                            {p.is_active ? (
                              <button onClick={() => handleDelete(p.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition ml-1" title="Disable plan">
                                <EyeOff className="w-4 h-4" />
                              </button>
                            ) : (
                              <button onClick={() => handleEnable(p.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-green-500 hover:bg-green-50 transition ml-1" title="Enable plan">
                                <Eye className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-slate-400">
            <Layers className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No plans found</p>
            <p className="text-sm mt-1">Click "Add Plan" to create your first package.</p>
          </div>
        )}
      </div>

      {/* Plan Modal */}
      {showModal && (
        <PlanModal form={form} setForm={setForm} onClose={closeModal} onSave={handleSave} editing={editing} groups={groups} />
      )}

      {/* Group Modal */}
      {showGroupModal && (
        <GroupModal gForm={gForm} setGForm={setGForm} onClose={closeGroupModal} onSave={handleSaveGroup} editing={editingGroup} />
      )}
    </div>
  );
}
